/**
 * Клиент черновиков ИИ-разбора (E11).
 *
 * Комплект уходит ОДНИМ запросом (multipart): договор и спецификации модель смотрит вместе —
 * именно так в спайке нашлись расхождения между документами. Разбор идёт фоном 19–73 секунды,
 * поэтому отправка отвечает сразу, а состояние экран узнаёт опросом самого черновика.
 *
 * Права — подсказка интерфейса, авторитет на сервере (правило 5): отправка, очередь и повтор —
 * `contract:register` (делопроизводитель/юрист/админ).
 */

import type { ApiResult } from "./contracts";

/**
 * Предельное время отправки комплекта, мс.
 *
 * Считается ТОЛЬКО загрузка файлов: сервер отвечает сразу, как файлы легли в хранилище, а сам
 * разбор идёт фоном. Две минуты — с запасом на несколько крупных сканов по медленному каналу;
 * всё, что дольше, это уже не «медленно», а «не дойдёт».
 */
const UPLOAD_TIMEOUT_MS = 120_000;

/** Состояние черновика — то же, что в базе; человеческие подписи ниже. */
export type DraftStatus =
  | "processing"
  | "ready"
  | "failed"
  | "confirmed"
  | "rejected"
  /** Отозван отправителем или юристом с причиной (D-24) — НЕ то же, что «отклонён». */
  | "withdrawn";

/** Файл комплекта. */
export interface DraftFileView {
  readonly id: string;
  readonly originalName: string;
  readonly contentType: string;
  readonly size: number;
  /** Файл был в запросе к модели. `false` — не влез в предел вложения, причина рядом. */
  readonly sentToAi: boolean;
  readonly skipReason: string | null;
}

export interface DraftView {
  readonly id: string;
  readonly status: DraftStatus;
  readonly payload: unknown;
  readonly notes: unknown;
  readonly provider: string | null;
  readonly model: string | null;
  readonly tokensIn: number | null;
  readonly tokensOut: number | null;
  readonly durationMs: number | null;
  readonly errorCode: string | null;
  readonly errorText: string | null;
  readonly attempts: number;
  readonly createdById: string;
  readonly createdByName: string | null;
  readonly verifiedById: string | null;
  readonly rejectReason: string | null;
  readonly contractId: string | null;
  /** Кто отозвал и почему — заполнены у состояния «отозван» (D-24). */
  readonly withdrawnById: string | null;
  readonly withdrawnByName: string | null;
  readonly withdrawReason: string | null;
  /** Кто взял черновик в работу и с какого момента; `null` — свободен (D-24). */
  readonly inWorkById: string | null;
  readonly inWorkByName: string | null;
  readonly inWorkSince: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly files: readonly DraftFileView[];
}

/** Доступность разбора: включён ли и, если нет, что показать человеку (план §3.12). */
export interface AiAvailability {
  readonly enabled: boolean;
  readonly disabledReason: string;
  /** Предупреждение о демонстрационном режиме (CR-4); пусто — режим боевой, плашки нет. */
  readonly demoNotice: string;
}

export interface DraftsQueue {
  readonly drafts: DraftView[];
  readonly ai: AiAvailability;
}

/**
 * Подписи состояний — СЛОВАМИ, как в плане §3.2. На экране не должно быть ни `processing`, ни
 * «статус 2»: делопроизводитель читает то же, что написано в памятке.
 */
export const DRAFT_STATUS_LABEL: Record<DraftStatus, string> = {
  processing: "В обработке",
  ready: "Готов к верификации",
  failed: "Ошибка — повторить",
  confirmed: "Подтверждён",
  rejected: "Отклонён",
  withdrawn: "Отозван",
};

/** Пояснение к состоянию: что происходит и чего ждать. */
export const DRAFT_STATUS_HINT: Record<DraftStatus, string> = {
  processing: "Идёт разбор — обычно меньше минуты. Страницу можно не держать открытой.",
  ready:
    "Разбор готов. Раскладку проверяет и подтверждает тот, кто принёс договор (Инициатор); " +
    "юрист проверяет выборочно.",
  failed: "Разбор не удался. Прочитайте причину: часть отказов лечится повтором, часть — нет.",
  confirmed: "Раскладка подтверждена — договор на контроле.",
  rejected: "Разбор отклонён с указанием причины.",
  // Разница с «отклонён» существенная и намеренно проговорена: отклонение — суждение
  // проверяющего о разборе, отзыв — «верифицировать было нечего».
  withdrawn: "Комплект отозван — верифицировать его не будут. Файлы сохранены.",
};

/**
 * Коды отказа разбора — СЛОВАМИ (D-51, дополнение владельца 13.09.2026).
 *
 * На экранах черновиков человек читает `errorText` — готовый текст сервера. Код нужен журналу
 * аудита: в записи об ошибке разбора сервер пишет именно его, и без словаря там стояло бы
 * `interrupted`. Словарь живёт здесь, рядом с подписями состояний, чтобы у кодов черновика был
 * один источник. Сервер знает коды в двух местах — тип `ParseErrorCode`
 * (apps/api/src/ai/provider.ts) и литералы при записи отказа (apps/api/src/drafts/runner.ts,
 * store.ts); полноту словаря против обоих держит сторож lib/audit-entities.test.ts.
 */
export const DRAFT_ERROR_CODE_LABEL: Record<string, string> = {
  provider_busy: "Провайдер ИИ перегружен",
  provider_timeout: "Провайдер ИИ не ответил вовремя",
  provider_auth: "Ключ провайдера ИИ не принят",
  provider_region: "Провайдер ИИ недоступен из региона сервера",
  model_unavailable: "Модель ИИ недоступна",
  bad_response: "Ответ модели не удалось разобрать",
  request_too_large: "Документ слишком большой для разбора",
  nothing_to_parse: "Нет файлов, пригодных для разбора",
  disabled: "Разбор выключен",
  // Не «в комплекте нет файлов»: файлы есть, но ни один не прочитан (все больше предела чтения).
  no_files: "Не удалось прочитать ни одного приложенного файла",
  internal: "Внутренняя ошибка при разборе",
  // Не «прерван перезапуском»: код ставит подбор зависших и при старте, и ежедневно — причиной
  // может быть и обрыв сети. Сервер сам пишет осторожно: «например, перезапуском сервера».
  interrupted: "Разбор прерван и не завершился",
};

/**
 * Отказы, которые кнопка «Повторить разбор» не лечит НИКОГДА (26.10.2026): тот же комплект соберёт
 * тот же запрос. `request_too_large` — тело запроса больше предела провайдера; повтор отказал бы
 * сразу, а юрист, увидев кнопку, решил бы, что дело в случайности. Выход у такого черновика
 * остаётся — «Отозвать комплект» (правило 22: тупиков нет).
 */
const RETRY_USELESS: ReadonlySet<string> = new Set(["request_too_large"]);

/** Показывать ли «Повторить разбор»: черновик упал, и повтор хоть что-то может изменить. */
export function canRetryParsing(draft: {
  readonly status: DraftStatus;
  readonly errorCode: string | null;
}): boolean {
  return draft.status === "failed" && !RETRY_USELESS.has(draft.errorCode ?? "");
}

/**
 * Штамп состояния черновика. У отказа, который повтор не лечит, — «Ошибка» без «— повторить»:
 * иначе штамп звал бы к кнопке, которой на экране нет.
 */
export function draftStatusLabel(draft: {
  readonly status: DraftStatus;
  readonly errorCode: string | null;
}): string {
  return draft.status === "failed" && !canRetryParsing(draft)
    ? "Ошибка"
    : DRAFT_STATUS_LABEL[draft.status];
}

/**
 * Провайдер разбора — по-русски, для журнала аудита (D-51, дополнение). Значения — те, что
 * записывает сервер: имя провайдера из ответа либо имя отказника при выключенном разборе.
 */
export const AI_PROVIDER_LABEL: Record<string, string> = {
  qwen: "Qwen через Yandex AI Studio",
  gemini: "Gemini (Google)",
  deepseek: "DeepSeek",
  fixture: "Записанный ответ (режим проверки)",
  disabled: "Разбор выключен",
};

/**
 * Закрытые состояния: работы по ним больше нет, и в активной очереди им не место.
 * Ровно тот же перечень, что у сервера в `listActive`.
 */
export function isClosed(status: DraftStatus): boolean {
  return status === "confirmed" || status === "rejected" || status === "withdrawn";
}

/** Конечное состояние: опрашивать больше нечего. */
export function isSettled(status: DraftStatus): boolean {
  return status !== "processing";
}

/** Отзыв возможен, пока по черновику нет решения (зеркало `WITHDRAWABLE` сервера). */
export function isWithdrawable(status: DraftStatus): boolean {
  return status === "processing" || status === "ready" || status === "failed";
}

/** Разрешены те же типы, что и для файлов договора (E6). */
export const DRAFT_ACCEPT = ".pdf,.jpg,.jpeg,.png,.docx";

/** Наибольшее число файлов в одном комплекте — зеркало сервера. */
export const MAX_DRAFT_FILES = 20;

async function toFailure(res: Response): Promise<Extract<ApiResult<never>, { ok: false }>> {
  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  const body = (await res.json().catch(() => null)) as { message?: string } | null;
  const message = body?.message;
  if (res.status === 403)
    return { ok: false, reason: "forbidden", message: message ?? "Недостаточно прав." };
  if (res.status === 404)
    return { ok: false, reason: "network", message: message ?? "Черновик не найден." };
  if (res.status === 400 || res.status === 409 || res.status === 413) {
    return { ok: false, reason: "validation", message: message ?? "Комплект не принят." };
  }
  return { ok: false, reason: "network", message: message ?? "Не удалось выполнить операцию." };
}

/**
 * Очередь черновиков вместе с состоянием разбора (GET /api/drafts).
 *
 * `includeClosed` — «показать закрытые»: к очереди добавляются подтверждённые, отклонённые и
 * отозванные. Умолчание — только незакрытые: очередь это список работы, а не история.
 */
export async function fetchDrafts(includeClosed = false): Promise<ApiResult<DraftsQueue>> {
  let res: Response;
  try {
    res = await fetch(includeClosed ? "/api/drafts?closed=1" : "/api/drafts", {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as Partial<DraftsQueue> | null;
  return {
    ok: true,
    value: {
      drafts: body?.drafts ?? [],
      // Молчание сервера о разборе трактуем как «выключен»: показать кнопку, за которой нет
      // разбора, хуже, чем не показать её при живом разборе.
      ai: body?.ai ?? {
        enabled: false,
        disabledReason: "Состояние ИИ-разбора неизвестно.",
        demoNotice: "",
      },
    },
  };
}

/** Один черновик (GET /api/drafts/:id) — им опрашивается ход разбора. */
export async function fetchDraft(id: string): Promise<ApiResult<DraftView>> {
  let res: Response;
  try {
    res = await fetch(`/api/drafts/${encodeURIComponent(id)}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { draft?: DraftView } | null;
  if (!body?.draft) {
    return { ok: false, reason: "network", message: "Сервер вернул неожиданный ответ." };
  }
  return { ok: true, value: body.draft };
}

/**
 * Отправка комплекта на разбор (POST /api/drafts, multipart).
 *
 * Все файлы — ОДНИМ запросом: разбитые на отдельные обращения, документы модель сравнить между
 * собой не сможет, и находки вроде «договор расходится со спецификацией» просто не появятся.
 */
export async function uploadDraft(files: readonly File[]): Promise<ApiResult<DraftView>> {
  const form = new FormData();
  for (const file of files) form.append("files", file, file.name);

  let res: Response;
  try {
    res = await fetch("/api/drafts", {
      method: "POST",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      body: form,
      // ОТПРАВКА ОБЯЗАНА ЧЕМ-ТО ЗАКАНЧИВАТЬСЯ (дефект приёмки 15.08.2026). Без срока запрос,
      // до которого сервер почему-либо не дошёл, оставляет экран в «отправке» навсегда: ни
      // успеха, ни ошибки, ни подсказки, что делать. Молчаливое ожидание хуже отказа — отказ
      // хотя бы называет следующий шаг.
      signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS),
    });
  } catch (err) {
    if (err instanceof Error && err.name === "TimeoutError") {
      return {
        ok: false,
        reason: "network",
        message:
          `Сервер не ответил за ${Math.round(UPLOAD_TIMEOUT_MS / 60_000)} мин — отправка ` +
          "прервана. Файлы могли не сохраниться: откройте очередь черновиков и проверьте, " +
          "появился ли комплект, прежде чем отправлять его заново.",
      };
    }
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { draft?: DraftView } | null;
  if (!body?.draft) {
    return { ok: false, reason: "network", message: "Сервер вернул неожиданный ответ." };
  }
  return { ok: true, value: body.draft };
}

/** Повтор разбора (POST /api/drafts/:id/retry) — только из состояния «ошибка». */
export async function retryDraft(id: string): Promise<ApiResult<DraftView>> {
  let res: Response;
  try {
    res = await fetch(`/api/drafts/${encodeURIComponent(id)}/retry`, {
      method: "POST",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { draft?: DraftView } | null;
  if (!body?.draft) {
    return { ok: false, reason: "network", message: "Сервер вернул неожиданный ответ." };
  }
  return { ok: true, value: body.draft };
}

/**
 * Действие над черновиком, отвечающее его новым состоянием: отзыв, захват, освобождение (D-24).
 *
 * Одна функция на три маршрута — они отличаются только адресом и телом. Три копии `fetch` с
 * одинаковой обработкой отказов разошлись бы на первой же правке текста ошибки.
 */
async function draftAction(
  id: string,
  action: "withdraw" | "take" | "release",
  input?: unknown,
): Promise<ApiResult<DraftView>> {
  let res: Response;
  try {
    res = await fetch(`/api/drafts/${encodeURIComponent(id)}/${action}`, {
      method: "POST",
      headers:
        input === undefined
          ? { Accept: "application/json" }
          : { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      ...(input === undefined ? {} : { body: JSON.stringify(input) }),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { draft?: DraftView } | null;
  if (!body?.draft) {
    return { ok: false, reason: "network", message: "Сервер вернул неожиданный ответ." };
  }
  return { ok: true, value: body.draft };
}

/**
 * Отзыв комплекта с причиной (POST /api/drafts/:id/withdraw).
 *
 * Кто и что вправе отозвать, решает СЕРВЕР (правило 5): отправитель — своё и только до захвата,
 * юрист — упавший разбор любого отправителя. Экран лишь не показывает кнопку там, где заведомо
 * откажут.
 */
export function withdrawDraft(id: string, reason: string): Promise<ApiResult<DraftView>> {
  return draftAction(id, "withdraw", { reason });
}

/** «Взять в работу» (POST /api/drafts/:id/take) — явный захват черновика юристом. */
export function takeDraftInWork(id: string): Promise<ApiResult<DraftView>> {
  return draftAction(id, "take");
}

/** «Освободить» (POST /api/drafts/:id/release) — снять свой захват, не решая по существу. */
export function releaseDraftInWork(id: string): Promise<ApiResult<DraftView>> {
  return draftAction(id, "release");
}

/**
 * Значение поля с цитатой и, если не сведено, причиной. У реквизитов договора и ставок неустойки
 * цитата поля пуста: она одна на блок (`DraftPrefill.contractCitation`, `penaltiesCitation`).
 */
export interface PrefillField<T> {
  readonly value: T | null;
  readonly citation: string | null;
  readonly note: string | null;
  /**
   * Поле пусто и после досылки: модель на повторной проверке ответила, что такого условия в
   * документах нет (E11b PR-6). Есть только у ключевых полей и только когда истинно.
   */
  readonly absentConfirmed?: true;
}

export interface PrefillCounterparty {
  readonly name: string | null;
  readonly inn: string | null;
  readonly kpp: string | null;
  readonly ogrn: string | null;
  readonly citation: string | null;
}

/**
 * Сторона договора из разбора — для выбора контрагента, когда наша сторона не опознана.
 * Заполняется сервером только в этом случае; `matchesOrg` он же и считает (одни правила с
 * направлением договора).
 */
export interface PrefillParty {
  readonly name: string | null;
  readonly inn: string | null;
  readonly kpp: string | null;
  readonly ogrn: string | null;
  readonly roleLabel: string | null;
  readonly citation: string | null;
  readonly matchesOrg: boolean;
}

export interface PrefillSpecification {
  readonly number: string | null;
  readonly date: string | null;
  readonly amount: string | null;
  readonly subject: string | null;
  readonly citation: string | null;
  readonly note: string | null;
}

export interface PrefillObligation {
  readonly type: string | null;
  readonly debtor: string | null;
  readonly descr: string | null;
  readonly amount: string | null;
  readonly ruleKind: string | null;
  readonly ruleDate: string | null;
  readonly ruleDays: number | null;
  readonly dayType: string | null;
  readonly ruleBase: string | null;
  readonly triggerDescription: string | null;
  /**
   * Обязательство-триггер, подобранное сервером по описанию события: место в этом же списке.
   * `null` — не подобрано, выбирает юрист. Подобранное юрист вправе сменить.
   */
  readonly triggerIndex: number | null;
  readonly deadlineHint: string | null;
  readonly citation: string | null;
}

/**
 * Подготовленная форма — её собирает СЕРВЕР (`drafts/prefill.ts`).
 *
 * Клиент ничего не пересчитывает: суммы и ставки модель отдаёт строкой «как в тексте», и
 * перевод их в наши формы написан один раз на сервере. Вторая реализация здесь разошлась бы с
 * первой молча — на пробеле, запятой или неразрывном пробеле.
 */
export interface DraftPrefill {
  readonly counterparty: PrefillCounterparty;
  /** Стороны на выбор — непусто ТОЛЬКО когда наша сторона не опознана. */
  readonly parties: readonly PrefillParty[];
  readonly direction: PrefillField<string>;
  readonly directionExplanation: string;
  /** Цитата блока «Договор» — одна на реквизиты, показывается под заголовком блока. */
  readonly contractCitation: string | null;
  /** Цитата блока «Неустойка» — одна на ставки и потолок, под подзаголовком блока. */
  readonly penaltiesCitation: string | null;
  readonly number: PrefillField<string>;
  readonly date: PrefillField<string>;
  readonly subject: PrefillField<string>;
  readonly validityTerm: PrefillField<string>;
  readonly price: PrefillField<string>;
  readonly penPayRate: PrefillField<string>;
  readonly penDelRate: PrefillField<string>;
  readonly penCapPercent: PrefillField<string>;
  readonly claimRequired: PrefillField<boolean>;
  readonly claimDays: PrefillField<number>;
  readonly court: PrefillField<string>;
  readonly specifications: readonly PrefillSpecification[];
  readonly obligations: readonly PrefillObligation[];
  readonly notes: readonly string[];
}

/**
 * Замечание к черновику: показывается юристу ДО кнопки подтверждения.
 *
 * Код — машинный признак, по нему экран решает, где показать замечание. Серверные коды —
 * закрытый перечень; экранные (`local:*`) собираются с указанием места, потому что одно и то же
 * препятствие бывает у нескольких строк сразу: три спецификации без даты — это три замечания, и
 * одинаковый код склеил бы их в одно.
 */
export interface DraftWarning {
  readonly code: string;
  readonly message: string;
  /** Блокирует подтверждение: там, где оно всё равно не пройдёт. */
  readonly blocking: boolean;
}

/** Карточка черновика для верификации: сам черновик и подготовленная форма. */
export interface DraftCard {
  readonly draft: DraftView;
  /** `null` — разбор ещё не готов или не удался: верифицировать нечего. */
  readonly prefill: DraftPrefill | null;
}

/** Ссылка на скан черновика — открывается обычной ссылкой, сессия идёт cookie (Р-5). */
export function draftFileUrl(draftId: string, fileId: string): string {
  return `/api/drafts/${encodeURIComponent(draftId)}/files/${encodeURIComponent(fileId)}`;
}

/**
 * Адрес того же файла ДЛЯ ПОКАЗА В СТРАНИЦЕ (D-47): панель просмотра рядом с полями разбора.
 *
 * Отличие от `draftFileUrl` — один признак в адресе, по которому сервер меняет заголовки
 * ответа со «скачать» на «показать». Права и проверка «свой черновик» те же самые: показ — это
 * те же данные тому же человеку, а не отдельный вход.
 */
export function draftFileViewUrl(draftId: string, fileId: string): string {
  return `${draftFileUrl(draftId, fileId)}?view=1`;
}

/**
 * Типы, которые панель просмотра показывает прямо в странице. Перечень повторяет серверный
 * (`apps/api/src/files/download.ts`) НЕ случайно: сервер решает, что отдать, а страница — что
 * показать, и оба обязаны отвечать одинаково. Разойдись они — пользователь увидит пустую рамку
 * там, где сервер прислал файл на скачивание.
 *
 * Совпадение двух списков стережёт тест `apps/api/tests/files/tipy-pokaza.test.ts`: он читает этот
 * файл текстом и сверяет перечень с серверным. Правите здесь — правьте и там, иначе прогон
 * покраснеет (и это лучше, чем пустая рамка у человека).
 */
export const VIEWABLE_TYPES: ReadonlySet<string> = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
]);

/** Карточка черновика с подготовленной формой (GET /api/drafts/:id). */
export async function fetchDraftCard(id: string): Promise<ApiResult<DraftCard>> {
  let res: Response;
  try {
    res = await fetch(`/api/drafts/${encodeURIComponent(id)}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as Partial<DraftCard> | null;
  if (!body?.draft) {
    return { ok: false, reason: "network", message: "Сервер вернул неожиданный ответ." };
  }
  return { ok: true, value: { draft: body.draft, prefill: body.prefill ?? null } };
}

/**
 * Замечания по ЧАСТИЧНОМУ телу (POST /api/drafts/:id/check).
 *
 * Зовётся, пока юрист правит поля: контрагент стоит на экране первым, и ждать заполненного
 * договора с разложенными обязательствами, чтобы сообщить о негодном ИНН, — значит вернуть ту
 * самую беду «узнаёт в конце».
 */
export async function checkDraft(id: string, partial: unknown): Promise<ApiResult<DraftWarning[]>> {
  let res: Response;
  try {
    res = await fetch(`/api/drafts/${encodeURIComponent(id)}/check`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(partial),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { warnings?: DraftWarning[] } | null;
  return { ok: true, value: body?.warnings ?? [] };
}

/** Подтверждение черновика (POST /api/drafts/:id/confirm) — договор на контроле. */
export async function confirmDraft(
  id: string,
  body: unknown,
): Promise<ApiResult<{ contractId: string }>> {
  let res: Response;
  try {
    res = await fetch(`/api/drafts/${encodeURIComponent(id)}/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const parsed = (await res.json().catch(() => null)) as { contractId?: string } | null;
  if (!parsed?.contractId) {
    return { ok: false, reason: "network", message: "Сервер вернул неожиданный ответ." };
  }
  return { ok: true, value: { contractId: parsed.contractId } };
}

/** Отклонение с причиной (POST /api/drafts/:id/reject) — причина обязательна (ТЗ 4.2.3). */
export async function rejectDraft(id: string, reason: string): Promise<ApiResult<DraftView>> {
  let res: Response;
  try {
    res = await fetch(`/api/drafts/${encodeURIComponent(id)}/reject`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ reason }),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { draft?: DraftView } | null;
  if (!body?.draft) {
    return { ok: false, reason: "network", message: "Сервер вернул неожиданный ответ." };
  }
  return { ok: true, value: body.draft };
}
